import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastrService } from 'ngx-toastr';

import {
  IAllSourceReferAPropertyUrlsItems,
  IProperty,
} from '@shared/models/properties';
import { SharePropertySource } from '@shared/models/share';
import { ShareService } from '@core/services/share.service';
import { PropertiesService } from '@core/services/properties.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';
import { selectAuthUser } from '@state/auth/auth.selectors';

const stripHtml = (html: string): string => {
  const textWithLineBreaks = html
    .replace(/&nbsp;/gi, ' ')
    .replace(/<br>/gi, '\n')
    .replace(/<div>/gi, '\n')
    .replace(/<li>/gi, '\n- ');
  const cleanText = textWithLineBreaks.replace(/<\/?[^>]+(>|$)/g, '');
  return cleanText.trim();
};

const capitalizeEachWord = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

@UntilDestroy()
@Component({
  selector: 'app-property-share-modal',
  templateUrl: './property-share-modal.component.html',
  styleUrls: ['./property-share-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyShareModalComponent implements OnInit, OnChanges {
  @Input({ required: true }) open = false;
  @Input({ required: true }) property: IProperty | undefined;
  @Input() fromMyDeals = false;
  @Output() handleClose = new EventEmitter<void>();

  protected shareEmails = false;
  protected shareSMS = false;
  protected fbUrl =
    'https://www.facebook.com/dialog/feed?app_id=1587162092149188&display=popup&redirect_uri=https://www.facebook.com';

  protected marketplaceName = 'Rebuilt Marketplace';
  protected loading = true;
  protected composedContent =
    'Check out this property on Rebuilt Marketplace: ';
  protected shareUrls: IAllSourceReferAPropertyUrlsItems | undefined;

  protected orgUrl: string | null | undefined;
  protected userOrgSlug: string | null | undefined;

  constructor(
    private shareService: ShareService,
    private clipboard: Clipboard,
    private propertiesService: PropertiesService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private toastr: ToastrService
  ) {}

  closeModal() {
    this.handleClose.emit();
  }

  ngOnInit(): void {
    this.store
      .select(selectOrganizationUrlSlug)
      .pipe(untilDestroyed(this))
      .subscribe((urlSlug) => {
        this.orgUrl = urlSlug?.toLowerCase();
      });

    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.userOrgSlug = user?.organization?.shortName?.toLowerCase();
        if (this.fromMyDeals && user?.organization?.name) {
          this.marketplaceName = `${user?.organization?.name}'s Marketplace`;
          this.composedContent = `Check out this property on ${this.marketplaceName}: `;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const previousValue = changes['open'].previousValue;
      const currentValue = changes['open'].currentValue;
      // When 'open' changes from false to true, reset the shareEmails state
      if (!previousValue && currentValue) {
        this.shareEmails = false;
      }
    }

    if (this.property) {
      // It can be from my deals or from the organization URL
      // If fromMyDeals don't check the organization URL
      const isPropertyFromOrgUrl =
        (this.fromMyDeals && !!this.userOrgSlug) ||
        (this.fromMyDeals &&
          this.orgUrl &&
          this.orgUrl ===
            this.property?.organization?.shortName?.toLowerCase());

      this.propertiesService
        .fetchAllSourceReferAPropertyUrls({
          propertyId: this.property.id,
          shortName:
            this.fromMyDeals && this.userOrgSlug
              ? this.userOrgSlug
              : this.orgUrl || null,
          type: isPropertyFromOrgUrl ? 'EXTERNAL' : null,
        })
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (data) => {
            const json = JSON.parse(data.allSourceReferAPropertyUrls);
            this.shareUrls = json;
            this.cdr.detectChanges();
          },
          error: () => {
            this.loading = false;
            this.cdr.detectChanges();
          },
          complete: () => {
            this.loading = false;
            this.cdr.detectChanges();
          },
        });
    }
  }

  async shareAPI(source: SharePropertySource) {
    if (this.property) {
      const input = {
        id: this.property.id,
        input: { source },
      };
      this.shareService.shareProperty(input).subscribe();
    }
  }

  getFormattedPrice(): string {
    if (!this.property) return '$0.00';
    if (!this.property.price.includes('$')) {
      // Format price to USD currency without cents if it doesn't include the dollar sign
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(parseFloat(this.property.price));
      return formattedPrice;
    } else {
      // Remove spaces if price already includes a dollar sign
      return this.property.price.replace(/\s+/g, '');
    }
  }

  shareFacebook() {
    if (this.property && this.shareUrls?.facebook) {
      let fbPostContent = `Check out this property on the ${this.marketplaceName}:`;
      fbPostContent += `\n${capitalizeEachWord(
        this.property.city
      )}, ${this.property.state.toUpperCase()} ${this.property.zip}`;

      fbPostContent += `\n\nAsking: ${this.getFormattedPrice()}`;
      fbPostContent +=
        `\n${this.property.bedrooms} Beds, ${this.property.bathrooms} ` +
        `Baths, ${this.property.squareFoot} Sqft`;
      if (this.property.yearBuilt) {
        fbPostContent += `, Built in ${this.property.yearBuilt}`;
      }

      if (this.property.dispositionAgent) {
        fbPostContent += `\n\nQuestions? Call ${capitalizeEachWord(
          this.property.dispositionAgent.normalizedFullName
        )} at ${this.property.dispositionAgent.phoneNumber}`;
      }

      if (this.property.description) {
        fbPostContent += `\n\nDescription:\n${stripHtml(
          this.property.description
        )}`;
      }
      if (this.property.repairs) {
        fbPostContent += `\n\nRepairs:\n${stripHtml(this.property.repairs)}`;
      }
      fbPostContent += `\n\n${this.shareUrls.facebook}`;

      this.clipboard.copy(fbPostContent);
      this.shareAPI('FACEBOOK');
      window.alert(
        'The property info has been copied to your clipboard. Facebook will open in a new tab, just paste the content into your post.'
      );
      window.open(this.fbUrl, '_blank');
    }
  }
}
